import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { authenticatedFetch } from "../context/apiClient";

//const API_BASE = "http://localhost:8000";

const SYSTEM_PROMPT = {
  role: "system",
  content: "You are a helpful, concise, and thoughtful assistant.",
};

function TypingDots() {
  return (
    <span className="typing-dots">
      <span /><span /><span />
    </span>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`msg-row ${isUser ? "user" : "assistant"}`}>
      <div className="avatar">{isUser ? "You" : "AI"}</div>
      <div className="bubble">
        {msg.content === "__typing__" ? <TypingDots /> : msg.content}
      </div>
    </div>
  );
}

export default function GenerateQuery() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState(null);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const { user, logout } = useAuth();

  const bottomRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const addMessage = (role, content) =>
    setMessages((prev) => [...prev, { role, content }]);

  const sendMessages = useCallback(
    async (history) => {
      setLoading(true);
      setError(null);

      const payload = [SYSTEM_PROMPT, ...history].map(({ role, content }) => ({
        role,
        content,
      }));

      setMessages((prev) => [...prev, { role: "assistant", content: "__typing__" }]);

      try {
        const res = await authenticatedFetch(`/api/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: payload }),
        }, user?.token, logout);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let assistantText = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n").filter((l) => l.startsWith("data: "));

          for (const line of lines) {
            const raw = line.replace("data: ", "").trim();
            if (raw === "[DONE]") break;
            try {
              const parsed = JSON.parse(raw);
              if (parsed.error) throw new Error(parsed.error);
              if (parsed.content) {
                assistantText += parsed.content;
                setMessages((prev) => {
                  const next = [...prev];
                  next[next.length - 1] = { role: "assistant", content: assistantText };
                  return next;
                });
              }
            } catch {}
          }
        }

        if (ttsEnabled && assistantText) {
          speak(assistantText);
        }
      } catch (e) {
        setError(e.message);
        setMessages((prev) => prev.slice(0, -1));
      } finally {
        setLoading(false);
      }
    },
    [ttsEnabled, user, logout]
  );

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const updated = [...messages, { role: "user", content: text }];
    addMessage("user", text);
    await sendMessages(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await handleTranscribe();
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      setError("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const form = new FormData();
      form.append("audio", blob, "recording.webm");
      const res = await authenticatedFetch(`/api/transcribe`, { method: "POST", body: form }, user?.token, logout);
      const data = await res.json();
      if (data.text) {
        setInput((prev) => prev + (prev ? " " : "") + data.text);
        textareaRef.current?.focus();
      }
    } catch {
      setError("Transcription failed.");
    } finally {
      setTranscribing(false);
    }
  };

  const speak = async (text) => {
    try {
      const res = await authenticatedFetch(`/api/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      }, user?.token, logout);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      new Audio(url).play();
    } catch {}
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  return (
    <div className="chat-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #f9f9f9;
          --surface: #ffffff;
          --border: #e5e7eb;
          --accent: #10a37f;
          --accent-light: rgba(16,163,127,0.1);
          --user-bg: #10a37f;
          --ai-bg: #ffffff;
          --text: #1a1a1a;
          --muted: #9ca3af;
          --danger: #dc2626;
          --radius: 12px;
        }

        .chat-container {
          font-family: 'Inter', sans-serif;
          background: var(--bg);
          color: var(--text);
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* ---------- HEADER ---------- */
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 24px;
          border-bottom: 1px solid var(--border);
          background: var(--surface);
          flex-shrink: 0;
        }
        .header-title {
          display: flex; align-items: center; gap: 8px;
          font-size: 15px; font-weight: 600; color: var(--text);
        }
        .header-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: var(--accent);
        }
        .header-actions { display: flex; gap: 8px; }
        .header-btn {
          background: transparent;
          border: 1px solid var(--border);
          color: #6b7280;
          padding: 5px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-family: inherit;
          cursor: pointer;
          transition: all .15s;
        }
        .header-btn:hover { color: var(--text); border-color: #9ca3af; background: #f3f4f6; }
        .tts-btn { display:flex;align-items:center;gap:5px; }
        .tts-btn.active { color: var(--accent); border-color: var(--accent); background: var(--accent-light); }

        /* ---------- MESSAGES ---------- */
        .messages {
          flex: 1;
          overflow-y: scroll;
          padding: 24px 0;
          background: var(--bg);
        }
        .messages::-webkit-scrollbar { width: 4px; }
        .messages::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

        .empty-state {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; height: 100%;
          gap: 10px; color: var(--muted);
        }
        .empty-icon { font-size: 36px; }
        .empty-state h2 { font-size: 17px; font-weight: 500; color: #374151; }
        .empty-state p { font-size: 13px; }

        .msg-row {
          display: flex;
          gap: 12px;
          padding: 8px 24px;
          max-width: 800px;
          margin: 0 auto;
          animation: fadeUp .2s ease;
        }
        @keyframes fadeUp {
          from { opacity:0; transform: translateY(6px); }
          to   { opacity:1; transform: translateY(0); }
        }
        .msg-row.user { flex-direction: row-reverse; }

        .avatar {
          flex-shrink: 0;
          width: 30px; height: 30px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 600;
          background: #e5e7eb;
          color: #6b7280;
        }
        .msg-row.user .avatar {
          background: var(--accent);
          color: #fff;
        }

        .bubble {
          max-width: 75%;
          padding: 10px 14px;
          border-radius: var(--radius);
          font-size: 14px;
          line-height: 1.65;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .msg-row.user .bubble {
          background: var(--user-bg);
          color: #fff;
          border-radius: var(--radius) var(--radius) 4px var(--radius);
        }
        .msg-row.assistant .bubble {
          background: var(--ai-bg);
          color: var(--text);
          border: 1px solid var(--border);
          border-radius: var(--radius) var(--radius) var(--radius) 4px;
        }

        /* Typing dots */
        .typing-dots { display:inline-flex; gap:4px; align-items:center; padding: 2px 0; }
        .typing-dots span {
          width:6px; height:6px; border-radius:50%;
          background: #9ca3af;
          animation: blink 1.2s infinite;
        }
        .typing-dots span:nth-child(2) { animation-delay:.2s; }
        .typing-dots span:nth-child(3) { animation-delay:.4s; }
        @keyframes blink { 0%,80%,100%{opacity:.25} 40%{opacity:1} }

        /* ---------- ERROR BAR ---------- */
        .error-bar {
          max-width: 800px; margin: 0 auto 8px;
          padding: 8px 14px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          font-size: 13px;
          color: var(--danger);
          display: flex; align-items: center; justify-content: space-between;
        }

        /* ---------- INPUT AREA ---------- */
        .input-area {
          padding: 12px 24px 16px;
          border-top: 1px solid var(--border);
          background: var(--surface);
          flex-shrink: 0;
        }
        .input-wrap {
          max-width: 800px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .input-row {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          background: #fff;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 8px 10px 8px 14px;
          transition: border-color .15s, box-shadow .15s;
        }
        .input-row:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-light); }

        textarea {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: var(--text);
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          resize: none;
          line-height: 1.5;
          max-height: 140px;
          overflow-y: auto;
        }
        textarea::placeholder { color: var(--muted); }

        .icon-btn {
          width: 32px; height: 32px;
          border-radius: 8px;
          border: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          font-size: 15px;
          transition: all .15s;
        }
        .mic-btn {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid var(--border);
        }
        .mic-btn:hover { background: #e5e7eb; color: var(--text); }
        .mic-btn.recording {
          background: #fef2f2;
          color: var(--danger);
          border-color: #fca5a5;
          animation: recordPulse 1s infinite;
        }
        @keyframes recordPulse { 0%,100%{box-shadow:none} 50%{box-shadow:0 0 8px rgba(220,38,38,.25)} }
        .mic-btn.transcribing { background: var(--accent-light); color: var(--accent); border-color: var(--accent); }

        .send-btn {
          background: var(--accent);
          color: #fff;
        }
        .send-btn:hover:not(:disabled) { background: #0d8f6f; }
        .send-btn:disabled { background: #d1d5db; color: #9ca3af; cursor: not-allowed; }

        .input-hint {
          font-size: 11px;
          color: var(--muted);
          text-align: center;
        }
      `}</style>

      {/* HEADER */}
      <header className="header">
        <div className="header-title">
          <div className="header-dot" />
          
        </div>
        <div className="header-actions">
         
          <button className="header-btn" onClick={clearChat}>Clear</button>
        </div>
      </header>

      {/* MESSAGES */}
      <div className="messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h2>Start a conversation</h2>
            <p>Type a message or press the mic to speak</p>
          </div>
        ) : (
          messages.map((msg, i) => <Message key={i} msg={msg} />)
        )}
        {error && (
          <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px" }}>
            <div className="error-bar">
              <span>⚠ {error}</span>
              <button style={{ background:"none",border:"none",cursor:"pointer",color:"inherit" }} onClick={() => setError(null)}>✕</button>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="input-area">
        <div className="input-wrap">
          <div className="input-row">
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder={transcribing ? "Transcribing…" : "Message here..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading || transcribing}
            />
            {/* MIC BUTTON */}
            <button
              className={`icon-btn mic-btn ${recording ? "recording" : ""} ${transcribing ? "transcribing" : ""}`}
              onClick={recording ? stopRecording : startRecording}
              disabled={loading || transcribing}
              title={recording ? "Stop recording" : "Start recording"}
            >
              {transcribing ? "⏳" : recording ? "⏹" : "🎙"}
            </button>
            {/* SEND BUTTON */}
            <button
              className="icon-btn send-btn"
              onClick={handleSend}
              disabled={!input.trim() || loading}
              title="Send message"
            >
              ↑
            </button>
          </div>
          <div className="input-hint">
            Enter to send · Shift+Enter for newline · 🎙 to speak
          </div>
        </div>
      </div>
    </div>
  );
}